import { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { PublicProjectFormResponse } from "../api/projectsApi";
import { ApiError } from "../types/Api";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import FormField from "./FormField";
import BrandedContainer from "./guest/BrandedContainer";

// Public field type from API response
interface PublicProjectField {
  id: string;
  name: string;
  label: string;
  type: string;
  required: boolean;
  multiple: boolean;
  order: number;
  help_text: string | null;
  confirm?: boolean;
}

interface PublicProjectFormProps {
  project: PublicProjectFormResponse;
  onSubmit: (formData: Record<string, string>) => Promise<void>;
}

// Define the form data type
type FormData = Record<string, string>;

export default function PublicProjectForm({
  project,
  onSubmit,
}: PublicProjectFormProps) {
  const { t } = useTranslation("gallery");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectUnavailable, setVideoLimitReached] = useState(false);

  // Use project colors from API
  const colors = project.colors;

  const topRef = useRef<HTMLDivElement>(null);

  // Safe scroll to top function to avoid DOM manipulation issues on Android
  const scrollToTop = () => {
    try {
      if (topRef.current) {
        topRef.current.scrollIntoView({ block: "start" });
      } else {
        // Fallback to window scroll without smooth behavior to avoid Android issues
        window.scrollTo(0, 0);
      }
    } catch (error) {
      console.warn("Scroll to top failed:", error);
      // Silent fail to prevent DOM errors
    }
  };

  // Dynamically create a yup validation schema based on the project fields
  const createValidationSchema = () => {
    const schemaShape: Record<string, yup.AnySchema> = {};

    project.fields.forEach((field) => {
      // Start with the base validation
      let fieldValidation: yup.AnySchema;

      // Set type-specific validation
      switch (field.type) {
        case "email":
          fieldValidation = yup
            .string()
            .test(
              "is-valid-email",
              `Please enter a valid email address`,
              (value) => {
                if (!value) return !field.required;
                if (field.multiple) {
                  return value
                    .split(",")
                    .every((email) =>
                      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
                        email.trim(),
                      ),
                    );
                }
                return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
              },
            );
          // Add confirmation field validation if confirm is enabled
          if (field.confirm) {
            schemaShape[`${field.id}_confirm`] = yup
              .string()
              .required("Please confirm your email address")
              .oneOf([yup.ref(field.id)], "Email addresses must match");
          }
          break;
        case "phone":
          fieldValidation = yup
            .string()
            .matches(
              /^\+?[0-9\s-()]{7,}$/,
              `Please enter a valid phone number`,
            );
          break;
        case "boolean":
          // For boolean fields, we handle required fields differently
          // A required boolean must be "true"
          fieldValidation = yup
            .string()
            .oneOf(["true", "false"], "Invalid value");

          if (field.required) {
            fieldValidation = fieldValidation.test(
              "is-true",
              `${field.label} must be checked`,
              (value) => value === "true",
            );
          }
          break;
        case "text":
        default:
          fieldValidation = yup.string();
          break;
      }

      // Add required validation if needed (except for boolean fields which are handled above)
      if (field.required && field.type !== "boolean") {
        fieldValidation = fieldValidation.required(
          `${field.label} is required`,
        );
      }

      // Add the field to the schema
      schemaShape[field.id] = fieldValidation;
    });

    return yup.object(schemaShape);
  };

  const validationSchema = createValidationSchema();

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    setError,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    // @ts-expect-error - The yupResolver has complex typing issues with dynamic schemas
    resolver: yupResolver(validationSchema),
    defaultValues: project.fields.reduce((acc, field) => {
      acc[field.id] = field.type === "boolean" ? "false" : "";
      // Add confirmation field default value if confirm is enabled
      if (field.type === "email" && field.confirm) {
        acc[`${field.id}_confirm`] = "";
      }
      return acc;
    }, {} as FormData),
  });

  const handleFormSubmit: SubmitHandler<FormData> = async (data) => {
    setIsSubmitting(true);
    setSubmitError("");

    try {
      await onSubmit(data);
      // Scroll BEFORE state update to avoid race condition with DOM cleanup
      scrollToTop();
      // Use RAF to ensure scroll completes before React re-renders
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );
      setSubmitSuccess(true);
    } catch (error: unknown) {
      console.error("Form submission error:", error);

      // Handle project unavailable (expired/not published) (403)
      if (error instanceof ApiError && error.status === 403) {
        setVideoLimitReached(true);
        requestAnimationFrame(() => scrollToTop());
        return;
      }

      if (error instanceof Error) {
        console.log("Error message:", error.message);

        // Handle specific error format with unique field validation
        if (error.message.includes('{"detail":')) {
          try {
            // Extract JSON from error message
            let jsonStr = error.message;
            if (!error.message.startsWith("{")) {
              const startIdx = error.message.indexOf("{");
              if (startIdx !== -1) {
                jsonStr = error.message.substring(startIdx);
              }
            }

            const jsonData = JSON.parse(jsonStr);
            console.log("Parsed error JSON:", jsonData);

            if (jsonData.detail?.unique_field_errors) {
              const uniqueFieldErrors = jsonData.detail
                .unique_field_errors as Record<string, string>;

              // Set errors for each field with react-hook-form
              Object.entries(uniqueFieldErrors).forEach(
                ([fieldId, errorMessage]) => {
                  setError(fieldId, {
                    type: "unique",
                    message: errorMessage,
                  });
                },
              );

              // Extract error messages for the alert
              const errorMessages = Object.values(uniqueFieldErrors);
              if (errorMessages.length === 1) {
                setSubmitError(errorMessages[0]);
              } else {
                setSubmitError(
                  `Multiple duplicate entries detected: ${errorMessages.join(
                    ", ",
                  )}`,
                );
              }

              // Return early
              return;
            }
          } catch (parseError) {
            console.error("Failed to parse error JSON:", parseError);
          }
        }

        // Fallback to using the error message directly
        setSubmitError(
          error.message || "Failed to submit form. Please try again.",
        );
      } else {
        setSubmitError("An unknown error occurred. Please try again.");
      }

      // Scroll to the top for error message using safer method
      requestAnimationFrame(() => scrollToTop());
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={topRef}>
      <BrandedContainer
        project={project}
        backgroundColor={colors.form_background}
        cardBackgroundColor={colors.form_background}
        textColor={colors.text_color}
      >
        {project.description && (
          <Box
            sx={{
              mb: 4,
              color: colors.text_color,
              "& p": { mb: 2 },
              "& ul, & ol": { mb: 2, pl: 3 },
              "& li": { mb: 1 },
              "& h1, & h2, & h3, & h4, & h5, & h6": { mb: 2, mt: 3 },
              "& blockquote": {
                borderLeft: "4px solid",
                borderColor: "divider",
                pl: 2,
                py: 1,
                my: 2,
                fontStyle: "italic",
              },
              "& pre": {
                backgroundColor: "grey.100",
                p: 2,
                borderRadius: 1,
                overflow: "auto",
                mb: 2,
              },
              "& code": {
                backgroundColor: "grey.100",
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontFamily: "monospace",
              },
            }}
            dangerouslySetInnerHTML={{ __html: project.description }}
          />
        )}

        {projectUnavailable ? (
          <Box
            key="unavailable"
            sx={{
              mb: 4,
              p: 4,
              backgroundColor: "#fff3e0",
              borderRadius: 2,
              textAlign: "center",
              border: "1px solid #ffcc80",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#ffe0b2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: "2rem",
              }}
            >
              ⓘ
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 2, color: "#e65100" }}
            >
              {t("public.eventEnded")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#bf360c", fontSize: "1.1rem", mb: 1 }}
            >
              {t("public.formUnavailable")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#795548" }}
            >
              {t("public.contactOrganizer")}
            </Typography>
          </Box>
        ) : submitSuccess ? (
          <Box
            key="success"
            sx={{
                mb: 4,
                p: 4,
                background: `linear-gradient(135deg, ${colors.button_gradient_start} 0%, ${colors.button_gradient_end} 100%)`,
                borderRadius: 2,
                textAlign: "center",
                color: colors.button_text_color,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)",
                },
              }}
            >
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                    fontSize: "2rem",
                  }}
                >
                  ✓
                </Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, mb: 2, color: "inherit" }}
                >
                  Successfully Submitted!
                </Typography>
                {project.conclusion ? (
                  <Box
                    sx={{
                      color: "inherit",
                      opacity: 0.9,
                      fontSize: "1.1rem",
                      "& p": { mb: 1 },
                      "& a": {
                        color: "inherit",
                        textDecoration: "underline",
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: project.conclusion }}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "inherit",
                      opacity: 0.9,
                      fontSize: "1.1rem",
                    }}
                  >
                    Thank you for your submission! We will review it and get
                    back to you soon.
                  </Typography>
                )}
              </Box>
            </Box>
        ) : (
          <Box
            key="form"
            component="form"
            onSubmit={handleSubmit(handleFormSubmit)}
            noValidate
          >
            <Stack spacing={3}>
              {/* Display unique field error message from the API */}
              {submitError && submitError.includes("already exists") && (
                <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Duplicate Entry Detected
                  </Typography>
                  <Typography variant="body2">{submitError}</Typography>
                </Alert>
              )}

              {/* Display other error messages */}
              {submitError && !submitError.includes("already exists") && (
                <Alert severity="error">{submitError}</Alert>
              )}

              {project.fields.map((field: PublicProjectField) => (
                <Controller
                  key={field.id}
                  name={field.id}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <FormField
                        field={field}
                        value={value}
                        error={
                          errors[field.id]?.message as string | undefined
                        }
                        onChange={onChange}
                        textColor={colors.text_color}
                        confirmValue={
                          field.confirm
                            ? watch(`${field.id}_confirm`)
                            : undefined
                        }
                        confirmError={
                          field.confirm
                            ? (errors[`${field.id}_confirm`]?.message as
                                | string
                                | undefined)
                            : undefined
                        }
                        onConfirmChange={
                          field.confirm
                            ? (val: string) => {
                                setValue(`${field.id}_confirm`, val, {
                                  shouldValidate: true,
                                });
                              }
                            : undefined
                        }
                      />
                      {field.confirm && (
                        <Controller
                          name={`${field.id}_confirm`}
                          control={control}
                          render={({ field: confirmField }) => (
                            <input type="hidden" {...confirmField} />
                          )}
                        />
                      )}
                    </>
                  )}
                />
              ))}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                fullWidth
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: colors.button_text_color,
                  background: `linear-gradient(135deg, ${colors.button_gradient_start} 0%, ${colors.button_gradient_end} 100%)`,
                  boxShadow: `0 4px 15px ${colors.button_gradient_start}66`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${colors.button_gradient_start}E6 0%, ${colors.button_gradient_end}E6 100%)`,
                    boxShadow: `0 6px 20px ${colors.button_gradient_start}80`,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    background: "rgba(0, 0, 0, 0.12)",
                    boxShadow: "none",
                  },
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress
                      size={24}
                      sx={{ mr: 1 }}
                      color="inherit"
                    />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </Stack>
          </Box>
        )}
      </BrandedContainer>
    </div>
  );
}
